import { useNavigate } from "react-router-dom";
import { Bell, CheckCircle, XCircle, Tray, Checks } from "@phosphor-icons/react";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { useNotifications, useMarkNotificationRead, useMarkAllNotificationsRead } from "@/lib/queries";

const TYPE_ICON = {
  output_approved: { icon: CheckCircle, cls: "text-success" },
  output_rejected: { icon: XCircle, cls: "text-destructive" },
  review_requested: { icon: Tray, cls: "text-primary" },
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function NotificationBell() {
  const navigate = useNavigate();
  const { data } = useNotifications();
  const markRead = useMarkNotificationRead();
  const markAll = useMarkAllNotificationsRead();
  const items = data?.items || [];
  const unread = data?.unread || 0;

  const open = (n) => {
    if (!n.read) markRead.mutate(n.id);
    if (n.link) navigate(n.link);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid="notification-bell"
          aria-label={`Notifications: ${unread} unread`}
          className="relative flex h-9 w-9 items-center justify-center border border-transparent transition-colors hover:border-border"
        >
          <Bell className="h-5 w-5 text-foreground" weight="bold" />
          {unread > 0 && (
            <span data-testid="notification-badge"
              className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center bg-accent-ai px-1 font-mono text-[10px] font-bold leading-none text-white">
              {unread > 99 ? "99+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 rounded-none p-0">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="font-mono text-[11px] uppercase tracking-wide text-muted-foreground">Notifications</p>
          {unread > 0 && (
            <button data-testid="notification-mark-all" onClick={() => markAll.mutate()}
              className="flex items-center gap-1 font-mono text-[11px] uppercase tracking-wide text-primary hover:underline">
              <Checks className="h-3.5 w-3.5" weight="bold" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto" data-testid="notification-list">
          {items.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Tray className="mx-auto h-7 w-7 text-muted-foreground" weight="duotone" />
              <p className="mt-2 text-sm text-muted-foreground">You're all caught up.</p>
            </div>
          ) : (
            items.map((n) => {
              const meta = TYPE_ICON[n.type] || { icon: Bell, cls: "text-muted-foreground" };
              const Icon = meta.icon;
              return (
                <button key={n.id} onClick={() => open(n)} data-testid="notification-item"
                  className={`flex w-full items-start gap-3 border-b border-border px-4 py-3 text-left transition-colors hover:bg-surface ${n.read ? "" : "bg-primary/[0.04]"}`}>
                  <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${meta.cls}`} weight="bold" />
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">{n.title}</p>
                    {n.body && <p className="truncate text-xs text-muted-foreground">{n.body}</p>}
                    <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">{timeAgo(n.created_at)}</p>
                  </div>
                  {!n.read && <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-accent-ai" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
